import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { createSelectors } from "./createSelectorsFunction";

export interface CompilationResult {
  status: string;
  errors: string;
  errorLines?: number[];
}

type CompilationState = {
  compilationResult: CompilationResult | null;
  isCompiling: boolean;
  isModalVisible: boolean;
  isFixingErrors: boolean;
  hasFixErrorsFailed: boolean;
  actions: {
    setCompilationResult: (result: CompilationResult | null) => void;
    setIsCompiling: (isCompiling: boolean) => void;
    clearCompilationResult: () => void;
    setModalVisibility: (isVisible: boolean) => void;
    setInitialCompilationState: () => void;
    setGeneratingStatus: () => void;
    setIsFixingErrors: (isFixingErrors: boolean) => void;
    setHasFixErrorsFailed: (hasFailed: boolean) => void;
  };
};

const initialState: Omit<CompilationState, 'actions'> = {
  compilationResult: null,
  isCompiling: false,
  isModalVisible: false,
  isFixingErrors: false,
  hasFixErrorsFailed: false,
};

const compilationStore = immer<CompilationState>((set: any) => ({
  ...initialState,
  actions: {
    setCompilationResult: (result: CompilationResult | null) => {
      // Extract line numbers from error messages if present
      if (result?.errors) {
        // Try different patterns for line numbers
        const patterns = [
          /line (\d+)/gi,  // "line X"
          /:(\d+):/gi,     // ":X:"
          /at line (\d+)/gi, // "at line X"
          /line (\d+):/gi  // "line X:"
        ];
        
        let errorLines: number[] = [];
        
        for (const pattern of patterns) {
          const matches = result.errors.match(pattern);
          if (matches) {
            errorLines = matches.map(match => {
              const lineNum = parseInt(match.replace(/[^0-9]/g, ''));
              return lineNum;
            });
            break; // Use the first pattern that matches
          }
        }
        
        result.errorLines = errorLines;
      }
      set({ compilationResult: result, isCompiling: false }, false, 'Set Compilation Result');
    },
    setIsCompiling: (isCompiling: boolean) => {
      set({ isCompiling, isModalVisible: true }, false, 'Set Is Compiling');
    },
    clearCompilationResult: () => {
      set({ compilationResult: null, isModalVisible: false, isFixingErrors: false, hasFixErrorsFailed: false }, false, 'Clear Compilation Result');
    },
    setModalVisibility: (isVisible: boolean) => {
      set({ isModalVisible: isVisible }, false, 'Set Modal Visibility');
    },
    setInitialCompilationState: () => {
      set({ 
        compilationResult: {
          status: "To be compiled",
          errors: "",
        },
        isModalVisible: false, // Non aprire automaticamente il modale
        isCompiling: false,
        hasFixErrorsFailed: false
      }, false, 'Set Initial Compilation State');
    },
    setGeneratingStatus: () => {
      set({ 
        compilationResult: {
          status: "Generating",
          errors: "",
        },
        isModalVisible: true,
        isCompiling: false
      }, false, 'Set Generating Status');
    },
    setIsFixingErrors: (isFixingErrors: boolean) => {
      set({ isFixingErrors }, false, 'Set Is Fixing Errors');
    },
    setHasFixErrorsFailed: (hasFailed: boolean) => {
      set({ hasFixErrorsFailed: hasFailed }, false, 'Set Has Fix Errors Failed');
    }
  },
}));

const useCompilationStoreBase = create(
  subscribeWithSelector(devtools(compilationStore, { name: 'CompilationStore' }))
);
const useCompilationStore = createSelectors(useCompilationStoreBase);

export const useCompilationStoreActions = () => useCompilationStore((state: any) => state.actions);

export default useCompilationStore; 